import { match } from 'utils/pattern-match-util'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetListItem,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetContentIllustration,
} from 'components/ModalSheet'
import Tooltip from 'components/Tooltip'
import { TextAreaContainer } from 'components/inputs'
import { observer } from 'mobx-react-lite'
import React from 'react'
import {
  RequestScopeChangesState,
  ScopeChangeCustomItem,
} from './RequestScopeChangesViewModel'
import JobViewModel from '../JobViewModel'
import styles from './OldRequestScopeChangesDialog.sass'
import cx from 'classnames'

interface OldRequestScopeChangesDialogProps {
  vm: JobViewModel
}

/**
 * The Old RequestScopeChangesDialog
 */
export default observer(function OldRequestScopeChangesDialog({
  vm: { requestScopeChangesVM: vm },
}: OldRequestScopeChangesDialogProps) {
  const disabled = vm.request.pending

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick={disabled}
      disableCloseOnEsc={disabled}
    >
      <ModalSheetHeader withCloseButton />
      {match(vm.state, {
        [RequestScopeChangesState.RequestingChanges]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <div className={styles.pager}>1 of 2</div>
                <ModalSheetContentTitle>
                  <div className={styles.header}>Request scope changes</div>
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  <div className={styles.header}>
                    Enter the service name and capture new form totals
                  </div>
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>

              <ModalSheetListItem>
                <div className={styles.listItem}>
                  <span>Service</span>
                  <span>
                    Total &nbsp;
                    <Tooltip
                      content={
                        'Total number of items being added or removed from the job specifications.'
                      }
                      placement="top"
                    >
                      <Icon name="info" size={18} color="var(--blue400)" />
                    </Tooltip>
                  </span>
                </div>
              </ModalSheetListItem>

              <div className={styles.customItemFieldSet}>
                {vm.scopeChangeCustomItems.map((item, index) => {
                  return (
                    <OldCustomScopeChangeInput
                      key={item.key}
                      item={item}
                      remove={vm.removeScopeChange}
                      dataTestId={index}
                    />
                  )
                })}

                <Button
                  type="text"
                  className={styles.addCustomItemButton}
                  onClick={vm.addScopeChange}
                  dataTest="request-scope-changes-add-button"
                >
                  Add Another Service
                </Button>
              </div>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  type="primary"
                  onClick={vm.moveToSubmissionStep}
                  disabled={vm.scopeChangeCustomItems.length === 0}
                  dataTest="request-scope-changes-next-button"
                >
                  Next
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.ReviewingChanges]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <div className={styles.pager}>2 of 2</div>
                <ModalSheetContentTitle>
                  <div className={styles.header}>Review changes</div>
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  <div className={styles.header}>
                    Confirm updates and share why changes are being requested
                  </div>
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>

              <ModalSheetListItem>
                <div className={styles.listItem}>
                  <span>Summary</span>
                  <span>
                    Total &nbsp;
                    <Tooltip
                      content={
                        'Total number of items being added or removed from the job specifications.'
                      }
                      placement="top"
                    >
                      <Icon name="info" size={18} color="var(--blue400)" />
                    </Tooltip>
                  </span>
                </div>
              </ModalSheetListItem>

              {vm.scopeChangeCustomItems.map((item) => (
                <ModalSheetListItem key={item.key}>
                  <div className={styles.listItem}>
                    <span>
                      <strong>{item.name}</strong>
                    </span>
                    <span>
                      <strong>{item.quantity}</strong>
                    </span>
                  </div>
                </ModalSheetListItem>
              ))}

              <div className={styles.listItemSeparator}></div>

              <ModalSheetContentBlock>
                <TextAreaContainer
                  label="Reason"
                  model={vm}
                  field="reason"
                  name="reason"
                  data-test="request-scope-changes-reason-input"
                  help={`${vm.reasonCharsRemaining} characters remaining.`}
                  error={vm.submissionValidationStep.getError('reason')}
                />
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  onClick={() =>
                    vm.setState(RequestScopeChangesState.RequestingChanges)
                  }
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  loading={vm.request.pending}
                  onClick={vm.request}
                  dataTest="request-scope-changes-submit-button"
                >
                  Send Request
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Success]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentIllustration
                  src={require('assets/images/img_welcome.svg')}
                />
                <ModalSheetContentTitle dataTest="request-scope-changes-success">
                  Thank you for your request
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  A Customer Success Representative will review and notify you
                  when updates have been approved.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={vm.close}>Close</Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.Failed]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Unable to request scope changes
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {vm.error ? (
                    vm.error
                  ) : (
                    <p>
                      We were unable to request scope changes, it's possible
                      that the request currently is not in a state where changes
                      are available, or it could be a transient error. Please
                      try again. If the issue persists, please contact an
                      administrator.
                    </p>
                  )}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button disabled={disabled} onClick={vm.close}>
                  Cancel
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [RequestScopeChangesState.UpdatingQuantity]:
          function (): React.JSX.Element {
            throw new Error('Function not implemented.')
          },
      })}
    </ModalSheet>
  )
})

const OldCustomScopeChangeInput = observer(function OldCustomScopeChangeInput({
  item,
  remove,
  dataTestId,
}: {
  item: ScopeChangeCustomItem
  remove: (key: string) => void
  dataTestId: number
}) {
  return (
    <div className={styles.customItemInput}>
      <IconButton
        className={styles.removeCustomItemButton}
        onClick={() => remove(item.key)}
        dataTest={`request-scope-changes-remove-button-${dataTestId}`}
      >
        <Icon name="close" size={28} />
      </IconButton>

      <input
        type="text"
        className={cx(
          styles.input,
          styles.customItemName,
          item.validation.getError('name') && styles.hasError
        )}
        placeholder="Enter your service type"
        value={item.name}
        onChange={(e) => item.setName(e.target.value)}
        data-test={`scope-change-name-${dataTestId}`}
      />

      <div
        className={cx(
          styles.customItemQuantityContainer,
          item.validation.getError('quantity') && styles.hasError
        )}
      >
        <IconButton>
          <Icon name="subtract" size={16} onClick={item.decrementQuantity} />
        </IconButton>

        <input
          key="quantity"
          type="number"
          min="1"
          className={cx(styles.input, styles.customItemQuantity)}
          value={item.quantity}
          onChange={(e) => item.setQuantity(e.target.value)}
          data-test={`scope-change-quantity-${dataTestId}`}
        />

        <IconButton onClick={item.incrementQuantity}>
          <Icon name="add" size={16} />
        </IconButton>
      </div>
    </div>
  )
})
